import { useEffect, useState } from "react";
import { CreditCard, Save, FileText } from "lucide-react";

import { api } from "@/lib/api-client";
import type { ExtraOptionRow, OrderSettingsBundle, OrderSettingsRow, PriceRow } from "@/lib/orders";
import { BTN_GHOST, BTN_PRIMARY } from "./ui";

const NUMBERS: Array<[keyof OrderSettingsRow, string]> = [
  ["vatPct", "VAT %"],
  ["depositPct", "Deposit %"],
  ["approvalDiscountPct", "Discount needing approval, above %"],
  ["validityDays", "Offer validity, days"],
  ["idRetentionDays", "Keep Emirates ID photos, days"],
];
const COMPANY: Array<[keyof OrderSettingsRow, string]> = [
  ["companyName", "Company name"],
  ["companyLine2", "Second line (branch of…)"],
  ["companyLicence", "Licence"],
  ["companyTrn", "TRN"],
  ["companyAddress", "Address"],
  ["companyPhone", "Phone"],
  ["companyEmail", "Email"],
  ["companyWeb", "Website"],
];

/** Settings → Orders: price list, extras, percentages, terms, company block. */
export function OrderSettingsPanel() {
  const [bundle, setBundle] = useState<OrderSettingsBundle | null>(null);
  const [settings, setSettings] = useState<OrderSettingsRow | null>(null);
  const [prices, setPrices] = useState<PriceRow[]>([]);
  const [extras, setExtras] = useState<ExtraOptionRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [stripeKey, setStripeKey] = useState("");
  const [stripeWebhook, setStripeWebhook] = useState("");
  const [stripeMsg, setStripeMsg] = useState<string | null>(null);

  const adopt = (b: OrderSettingsBundle) => {
    setBundle(b);
    setSettings(b.settings);
    setPrices(b.priceList);
    setExtras(b.extraOptions);
  };

  useEffect(() => {
    api.orderSettings().then(adopt).catch(() => setMsg("Could not load"));
  }, []);

  if (!bundle || !settings) return <div className="mt-4 text-sm text-ice/60">{msg ?? "Loading…"}</div>;

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      adopt(
        await api.updateOrderSettings({
          settings: { ...settings, ...(stripeKey.trim() ? { stripeSecretKey: stripeKey.trim() } : {}), ...(stripeWebhook.trim() ? { stripeWebhookSecret: stripeWebhook.trim() } : {}) },
          priceList: prices.map(({ finish, label, floorPrice, wallPrice, active }) => ({ finish, label, floorPrice: Number(floorPrice), wallPrice: Number(wallPrice), active })),
          extraOptions: extras.map(({ code, label, kind, pct, active }) => ({ code, label, kind, pct: kind === "percent" ? Number(pct ?? 0) : null, active })),
        }),
      );
      setStripeKey("");
      setStripeWebhook("");
      setMsg("Saved");
    } catch {
      setMsg("Could not save");
    } finally {
      setBusy(false);
    }
  };

  const num = (v: unknown) => (v === "" || v === null ? "" : String(v));

  return (
    <div className="mt-4 space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-ice/70">Prices and extras feed new orders; existing orders keep their own copy.</p>
        <button type="button" className={BTN_PRIMARY} disabled={busy} onClick={save}>
          <Save className="h-3.5 w-3.5" /> {busy ? "Saving…" : "Save"}
        </button>
      </div>
      {msg && <p className="text-xs text-goldbright">{msg}</p>}

      <section className="glass-panel overflow-x-auto p-4">
        <div className="brand-kicker mb-2">Price list · AED / m²</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[0.6rem] uppercase tracking-[0.18em] text-ice/50">
              <th className="py-2 pr-3">Finish</th><th className="py-2 pr-3">Label</th><th className="py-2 pr-3">Floor</th><th className="py-2 pr-3">Wall</th><th className="py-2">Active</th>
            </tr>
          </thead>
          <tbody>
            {prices.map((p, i) => (
              <tr key={p.finish} className="border-t border-white/8">
                <td className="py-1.5 pr-3 text-ice/60">{p.finish}</td>
                <td className="py-1.5 pr-3"><input className="field" value={p.label} onChange={(e) => setPrices(prices.map((r, j) => (j === i ? { ...r, label: e.target.value } : r)))} /></td>
                <td className="py-1.5 pr-3"><input className="field num w-28" type="number" dir="ltr" value={num(p.floorPrice)} onChange={(e) => setPrices(prices.map((r, j) => (j === i ? { ...r, floorPrice: Number(e.target.value) } : r)))} /></td>
                <td className="py-1.5 pr-3"><input className="field num w-28" type="number" dir="ltr" value={num(p.wallPrice)} onChange={(e) => setPrices(prices.map((r, j) => (j === i ? { ...r, wallPrice: Number(e.target.value) } : r)))} /></td>
                <td className="py-1.5"><input type="checkbox" className="h-4 w-4 accent-[#D6338F]" checked={p.active} onChange={(e) => setPrices(prices.map((r, j) => (j === i ? { ...r, active: e.target.checked } : r)))} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="glass-panel overflow-x-auto p-4">
        <div className="brand-kicker mb-2">Extras</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[0.6rem] uppercase tracking-[0.18em] text-ice/50">
              <th className="py-2 pr-3">Code</th><th className="py-2 pr-3">Label</th><th className="py-2 pr-3">Kind</th><th className="py-2 pr-3">%</th><th className="py-2">Active</th>
            </tr>
          </thead>
          <tbody>
            {extras.map((x, i) => (
              <tr key={x.code} className="border-t border-white/8">
                <td className="py-1.5 pr-3 text-ice/60">{x.code}</td>
                <td className="py-1.5 pr-3"><input className="field" value={x.label} onChange={(e) => setExtras(extras.map((r, j) => (j === i ? { ...r, label: e.target.value } : r)))} /></td>
                <td className="py-1.5 pr-3">
                  <select className="field" value={x.kind} onChange={(e) => setExtras(extras.map((r, j) => (j === i ? { ...r, kind: e.target.value as ExtraOptionRow["kind"] } : r)))}>
                    {["percent", "included", "on_request"].map((k) => <option key={k} value={k} className="bg-night">{k}</option>)}
                  </select>
                </td>
                <td className="py-1.5 pr-3"><input className="field num w-20" type="number" dir="ltr" disabled={x.kind !== "percent"} value={num(x.pct)} onChange={(e) => setExtras(extras.map((r, j) => (j === i ? { ...r, pct: Number(e.target.value) } : r)))} /></td>
                <td className="py-1.5"><input type="checkbox" className="h-4 w-4 accent-[#D6338F]" checked={x.active} onChange={(e) => setExtras(extras.map((r, j) => (j === i ? { ...r, active: e.target.checked } : r)))} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="glass-panel p-4">
        <div className="brand-kicker mb-2">Rules</div>
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {NUMBERS.map(([k, label]) => (
            <label key={k} className="block">
              <span className="field-label">{label}</span>
              <input className="field num" type="number" dir="ltr" value={num(settings[k])} onChange={(e) => setSettings({ ...settings, [k]: Number(e.target.value) })} />
            </label>
          ))}
        </div>
        <div className="mt-3">
          <span className="field-label">Quantity brackets (upper bound m² → %, last one open)</span>
          <div className="flex flex-wrap gap-2">
            {settings.brackets.map((b, i) => (
              <div key={i} className="flex items-center gap-1 rounded-lg border border-white/10 p-1">
                <input className="field num w-24" type="number" dir="ltr" placeholder="∞" value={b.maxSqm ?? ""} onChange={(e) => setSettings({ ...settings, brackets: settings.brackets.map((r, j) => (j === i ? { ...r, maxSqm: e.target.value === "" ? null : Number(e.target.value) } : r)) })} />
                <span className="text-ice/50">→</span>
                <input className="field num w-20" type="number" dir="ltr" value={b.pct} onChange={(e) => setSettings({ ...settings, brackets: settings.brackets.map((r, j) => (j === i ? { ...r, pct: Number(e.target.value) } : r)) })} />
                <span className="text-ice/50">%</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="glass-panel p-4">
        <div className="brand-kicker mb-2">Systems · AED / m²</div>
        <div className="grid gap-3 sm:grid-cols-4">
          <label className="block">
            <span className="field-label">Geopolymer: microcement list + %</span>
            <input className="field num" type="number" dir="ltr" value={num(settings.geopolymerPct)} onChange={(e) => setSettings({ ...settings, geopolymerPct: Number(e.target.value) })} />
          </label>
          {(["chromatic", "metallic", "art"] as const).map((k) => (
            <label key={k} className="block">
              <span className="field-label">{k} epoxy</span>
              <input className="field num" type="number" dir="ltr" value={num(settings.epoxyPrices?.[k])} onChange={(e) => setSettings({ ...settings, epoxyPrices: { ...settings.epoxyPrices, [k]: Number(e.target.value) } })} />
            </label>
          ))}
        </div>
        <p className="mt-2 text-xs text-ice/50">Microcement uses the price list above. Epoxy rates apply to floor and wall alike.</p>
      </section>

      <section className="glass-panel overflow-x-auto p-4">
        <div className="brand-kicker mb-2">Bathroom packages · fixed prices AED</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[0.6rem] uppercase tracking-[0.18em] text-ice/50">
              <th className="py-2 pr-3">Size</th><th className="py-2 pr-3">Label</th><th className="py-2 pr-3">Floor</th><th className="py-2">Wall</th>
            </tr>
          </thead>
          <tbody>
            {settings.bathroomPackages.map((p, i) => (
              <tr key={p.code} className="border-t border-white/8">
                <td className="py-1.5 pr-3 text-ice/60">{p.code}</td>
                <td className="py-1.5 pr-3"><input className="field" value={p.label} onChange={(e) => setSettings({ ...settings, bathroomPackages: settings.bathroomPackages.map((r, j) => (j === i ? { ...r, label: e.target.value } : r)) })} /></td>
                <td className="py-1.5 pr-3"><input className="field num w-28" type="number" dir="ltr" value={num(p.floor)} onChange={(e) => setSettings({ ...settings, bathroomPackages: settings.bathroomPackages.map((r, j) => (j === i ? { ...r, floor: Number(e.target.value) } : r)) })} /></td>
                <td className="py-1.5"><input className="field num w-28" type="number" dir="ltr" value={num(p.wall)} onChange={(e) => setSettings({ ...settings, bathroomPackages: settings.bathroomPackages.map((r, j) => (j === i ? { ...r, wall: Number(e.target.value) } : r)) })} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-xs text-ice/50">Extras apply on top of the package price. Packages are never adjusted by the quantity bracket.</p>
      </section>

      <section className="glass-panel p-4">
        <div className="brand-kicker mb-2">Terms printed on the order</div>
        <textarea className="field min-h-32" value={settings.termsEn} onChange={(e) => setSettings({ ...settings, termsEn: e.target.value })} />
      </section>

      <section className="glass-panel p-4">
        <div className="brand-kicker mb-2">Payment details printed on the order</div>
        <textarea className="field min-h-40 font-mono text-xs" value={settings.paymentDetails ?? ""} onChange={(e) => setSettings({ ...settings, paymentDetails: e.target.value || null })} />
      </section>

      <section className="glass-panel p-4">
        <div className="brand-kicker mb-2"><CreditCard className="mr-1 inline h-3.5 w-3.5" /> Payments · Stripe</div>
        <p className="text-xs text-ice/60">
          {settings.stripe?.configured
            ? <>Connected in <b className="text-paper">{settings.stripe.mode}</b> mode, key ending <b className="text-paper">{settings.stripe.keyLast4}</b>. Webhook secret {settings.stripe.webhookSet ? <b className="text-green">set</b> : <b className="text-pink">missing</b>}.</>
            : "Not configured. Paste the secret key from the Stripe dashboard; test keys (sk_test_…) work before the account is approved."}
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="field-label">Secret key (sk_… or rk_…) · write-only</span>
            <input className="field" type="password" autoComplete="off" placeholder={settings.stripe?.configured ? "leave empty to keep the current key" : "sk_test_…"} value={stripeKey} onChange={(e) => setStripeKey(e.target.value)} />
          </label>
          <label className="block">
            <span className="field-label">Webhook signing secret (whsec_…) · write-only</span>
            <input className="field" type="password" autoComplete="off" placeholder={settings.stripe?.webhookSet ? "leave empty to keep" : "whsec_…"} value={stripeWebhook} onChange={(e) => setStripeWebhook(e.target.value)} />
          </label>
        </div>
        <p className="mt-2 text-xs text-ice/60">Webhook endpoint to register in Stripe (events <code>checkout.session.completed</code>, <code>checkout.session.expired</code>): <code className="select-all text-paper">{settings.stripe?.webhookUrl}</code></p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            className={BTN_PRIMARY}
            disabled={busy || (!stripeKey.trim() && !stripeWebhook.trim())}
            onClick={async () => {
              setBusy(true);
              setStripeMsg("Saving keys…");
              try {
                adopt(await api.updateOrderSettings({ settings: { ...(stripeKey.trim() ? { stripeSecretKey: stripeKey.trim() } : {}), ...(stripeWebhook.trim() ? { stripeWebhookSecret: stripeWebhook.trim() } : {}) } }));
                setStripeKey("");
                setStripeWebhook("");
                setStripeMsg("Keys saved. Now press Test connection.");
              } catch (e) {
                let why = "Could not save the keys.";
                try { const m = (JSON.parse((e as Error).message) as { message?: string }).message; if (m) why = m; } catch { /* keep default */ }
                setStripeMsg(why);
              } finally {
                setBusy(false);
              }
            }}
          >
            Save keys
          </button>
          <button type="button" className={BTN_GHOST} disabled={busy || !settings.stripe?.configured} onClick={async () => { setStripeMsg("Testing…"); try { const r = await api.stripeTest(); setStripeMsg(`Stripe answers: ${r.livemode ? "LIVE" : "test"} mode, currencies ${r.currencies.join(", ") || "none yet"}.`); } catch { setStripeMsg("Stripe refused the key. Check it and save again."); } }}>
            Test connection
          </button>
          {settings.stripe?.configured && (
            <button type="button" className="text-xs text-pink underline underline-offset-4" disabled={busy} onClick={async () => { if (!window.confirm("Remove the Stripe keys from Surface ID?")) return; setBusy(true); try { adopt(await api.updateOrderSettings({ settings: { ...settings, stripeClear: true } as unknown as OrderSettingsRow })); setStripeMsg("Stripe keys removed."); } finally { setBusy(false); } }}>
              Remove keys
            </button>
          )}
          {stripeMsg && <span className="text-xs text-goldbright">{stripeMsg}</span>}
        </div>
        <p className="mt-2 text-xs text-ice/50">Keys are stored encrypted and never shown again.</p>
      </section>

      <section className="glass-panel p-4">
        <div className="brand-kicker mb-2"><FileText className="mr-1 inline h-3.5 w-3.5" /> Invoices in i-GlooCRM</div>
        <p className="mb-3 text-xs text-ice/60">
          Each paid instalment becomes an invoice in the CRM (deposit → Acconto, balance → Saldo), under the customer's account. The series must exist in the CRM (Settings → Warehouse/doc series, module Invoice) and the product is used for the single invoice line.
        </p>
        {(() => {
          const c = settings.crmInvoice ?? { enabled: false, auto: true, bu: "Galassia Holding", docType: "", agentId: 1, assignedUserId: 1, productId: null, currencyId: 3, currencyRate: 4.3 };
          const setC = (patch: Partial<typeof c>) => setSettings({ ...settings, crmInvoice: { ...c, ...patch } });
          const numField = (k: "agentId" | "assignedUserId" | "productId" | "currencyId", label: string, hint: string) => (
            <label className="block">
              <span className="field-label">{label}</span>
              <input className="field" inputMode="numeric" value={c[k] ?? ""} placeholder={hint} onChange={(e) => setC({ [k]: e.target.value === "" ? (k === "productId" ? null : 0) : Number(e.target.value) } as Partial<typeof c>)} />
            </label>
          );
          return (
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex items-center gap-2 text-sm text-ice/80 sm:col-span-2">
                <input type="checkbox" checked={c.enabled} onChange={(e) => setC({ enabled: e.target.checked })} /> Push invoices to the CRM
              </label>
              <label className="flex items-center gap-2 text-sm text-ice/80 sm:col-span-2">
                <input type="checkbox" checked={c.auto} onChange={(e) => setC({ auto: e.target.checked })} /> Automatically when a payment is marked paid
              </label>
              <label className="block">
                <span className="field-label">Company (bu_mc)</span>
                <input className="field" value={c.bu} onChange={(e) => setC({ bu: e.target.value })} placeholder="Galassia Holding" />
              </label>
              <label className="block">
                <span className="field-label">Invoice series (doc_invoice)</span>
                <input className="field" value={c.docType} onChange={(e) => setC({ docType: e.target.value })} placeholder="e.g. VERSUS" />
              </label>
              {numField("productId", "CRM product id for the line", "e.g. 2446999")}
              {numField("agentId", "Commercial agent (CRM user id)", "1")}
              {numField("assignedUserId", "Assigned to (CRM user id)", "1")}
              {numField("currencyId", "Currency id", "3 = AED")}
              <label className="block">
                <span className="field-label">Rate vs the CRM base currency (EUR)</span>
                <input className="field" inputMode="decimal" value={c.currencyRate ?? ""} placeholder="4.3" onChange={(e) => setC({ currencyRate: Number(e.target.value) || 0 })} />
              </label>
            </div>
          );
        })()}
      </section>

      <section className="glass-panel p-4">
        <div className="brand-kicker mb-2">Company block (footer)</div>
        <div className="grid gap-3 sm:grid-cols-2">
          {COMPANY.map(([k, label]) => (
            <label key={k} className="block">
              <span className="field-label">{label}</span>
              <input className="field" value={(settings[k] as string | null) ?? ""} onChange={(e) => setSettings({ ...settings, [k]: e.target.value || null })} />
            </label>
          ))}
        </div>
      </section>
    </div>
  );
}
